import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GridOnIcon from '@mui/icons-material/GridOn';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TableViewIcon from '@mui/icons-material/TableView';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PublishIcon from '@mui/icons-material/Publish';
import SaveIcon from '@mui/icons-material/Save';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ViewListIcon from '@mui/icons-material/ViewList';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { collectConditions, describeRule, validateDefinition, type FieldType, type FormField } from '@re/form-engine';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { usePlatformLookups } from '../../api/lookups';
import type { DefinitionReport, FormSummary, FormVersion } from '../../api/types';
import { Button } from '../../components/button';
import { ConfirmDialog, ErrorState, Loading } from '../../components/common';
import { PageActionsMenu, type ActionMenuItem } from '../../components/page-actions-menu';
import { useToast } from '../../components/Toast';
import { fmtDate } from '../../lib/labels';
import { FieldEditor } from './FieldEditor';
import { FieldList } from './FieldList';
import { AddFieldDialog } from './AddFieldDialog';
import { FormPreviewPane } from './FormPreviewPane';
import { openFormPreview } from './FormPreviewPage';
import { VisibilityMatrix } from './VisibilityMatrix';
import { ScenarioView } from './ScenarioView';
import { AUTO_FIXABLE, newField, renumber, translationIssues, withMandatoryFields } from './helpers';
import { downloadFormJson, readFormJson } from './formJson';
import { BilingualScope, BilingualToolbar } from '../../components/bilingual';
import { isEn, tr } from '../../lib/i18n';

interface Draft {
  id: string;
  version_no: number;
  notes: string | null;
  fields: FormField[];
  validation: DefinitionReport;
}

const PREVIEW_KEY = 'platform.builder.preview';
const VIEW_KEY = 'platform.builder.view';
type View = 'build' | 'matrix' | 'scenario';
const readPref = () => {
  try {
    return localStorage.getItem(PREVIEW_KEY) !== '0';
  } catch {
    return true;
  }
};

/** A pane of the builder: its own scroll, so the list, the editor and the preview move independently. */
function Pane({ title, actions, children, sx }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; sx?: object }) {
  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, borderRadius: 3, overflow: 'hidden', ...sx }}>
      {title !== undefined && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, minHeight: 48, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>{title}</Typography>
          {actions}
        </Stack>
      )}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: { md: 'auto' }, p: 1.5, display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Paper>
  );
}

export function FormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const lookups = usePlatformLookups();

  const forms = useQuery({ queryKey: ['forms'], queryFn: () => api<{ items: FormSummary[] }>('/forms') });
  const form = forms.data?.items.find((f) => f.id === id);
  const draftQ = useQuery({
    queryKey: ['form-draft', id],
    queryFn: async () => {
      try {
        return await api<Draft>(`/forms/${id}/draft`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
  const publishedQ = useQuery({
    queryKey: ['form-version', form?.published_version_id],
    queryFn: () => api<{ fields: FormField[] }>(`/forms/versions/${form!.published_version_id}`),
    enabled: !!form?.published_version_id && draftQ.data === null,
  });

  const [fields, setFields] = useState<FormField[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [serverReport, setServerReport] = useState<DefinitionReport | null>(null);
  const [view, setViewState] = useState<View>(() => {
    try {
      const v = localStorage.getItem(VIEW_KEY);
      return v === 'matrix' || v === 'scenario' ? v : 'build';
    } catch {
      return 'build';
    }
  });
  const setView = (v: View) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* ignore */
    }
  };
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [previewOn, setPreviewOn] = useState(readPref);
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<'publish' | 'discard' | 'delete' | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewDrawer, setPreviewDrawer] = useState(false);
  useEffect(() => {
    if (draftQ.data) {
      // an empty new form starts with the mandatory fields (request type, name, phone)
      const empty = draftQ.data.fields.length === 0;
      const initial = empty ? withMandatoryFields([]) : draftQ.data.fields;
      setFields(initial);
      setServerReport(draftQ.data.validation);
      setDirty(empty);
      setSelected((s) => s ?? initial[0]?.key ?? null);
    }
  }, [draftQ.data]);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // Ctrl/⌘+S saves the draft
  const saveRef = useCallback(() => {
    if (draftQ.data && dirty && !save.isPending) save.mutate();
  }, [draftQ.data, dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [saveRef]);

  // live engine validation (instant); the server adds lookup-code checks on save
  const live = useMemo(() => validateDefinition(fields), [fields]);
  const translation = useMemo(() => translationIssues(fields), [fields]);
  const codeIssues = (serverReport?.errors ?? []).filter((e) => e.code.startsWith('RULE_CODE'));
  const allErrors = [...live.errors, ...translation, ...(dirty ? [] : codeIssues)];
  const issueKeys = new Set(allErrors.map((e) => e.field).filter(Boolean) as string[]);

  const update = (next: FormField[]) => {
    next = next.map((f) => (f.type === 'request_type' && !f.required ? { ...f, required: true } : f));
    setFields(next);
    setDirty(true);
  };
  const current = fields.find((f) => f.key === selected) ?? null;

  const save = useMutation({
    mutationFn: () => api<{ fields: FormField[]; validation: DefinitionReport }>(`/forms/${id}/draft/fields`, { method: 'PUT', body: { fields } }),
    onSuccess: (r) => {
      setFields(r.fields);
      setServerReport(r.validation);
      setDirty(false);
      qc.setQueryData(['form-draft', id], (d: Draft | null | undefined) => (d ? { ...d, fields: r.fields, validation: r.validation } : d));
      toast(tr('تم حفظ المسودة', 'Draft saved'));
    },
    onError: toastError,
  });
  const createDraft = useMutation({
    mutationFn: () => api(`/forms/${id}/draft`, { method: 'POST', body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form-draft', id] });
      qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: toastError,
  });
  const publish = useMutation({
    mutationFn: async () => {
      if (dirty) await api(`/forms/${id}/draft/fields`, { method: 'PUT', body: { fields } });
      return api<{ version: number; warnings: { message: string }[] }>(`/forms/${id}/draft/publish`, { method: 'POST', body: {} });
    },
    onSuccess: (r) => {
      toast(tr(`تم نشر النسخة v${r.version}`, `Version v${r.version} published`));
      setConfirm(null);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['form-draft', id] });
      qc.invalidateQueries({ queryKey: ['forms'] });
      qc.invalidateQueries({ queryKey: ['form-versions', id] });
    },
    onError: (e) => {
      setConfirm(null);
      toastError(e);
    },
  });
  const discard = useMutation({
    mutationFn: () => api(`/forms/${id}/draft`, { method: 'DELETE' }),
    onSuccess: () => {
      toast(tr('تم حذف المسودة', 'Draft deleted'));
      setConfirm(null);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['form-draft', id] });
      qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: toastError,
  });

  const addField = (type: FieldType) => {
    setAdding(false);
    const f = newField(type, fields);
    const at = current ? fields.findIndex((x) => x.key === current.key) + 1 : fields.length;
    const next = renumber([...fields.slice(0, at), f, ...fields.slice(at)]);
    update(next);
    setSelected(f.key);
  };
  const duplicate = (key: string) => {
    const src = fields.find((f) => f.key === key);
    if (!src || src.type === 'request_type') return;
    let k = `${src.key}_copy`;
    for (let n = 2; fields.some((f) => f.key === k); n++) k = `${src.key}_copy${n}`;
    const copy: FormField = {
      ...JSON.parse(JSON.stringify(src)),
      key: k,
      mapsTo: null, // a system column belongs to one field only
      label: { ar: src.label.ar ? `${src.label.ar} (نسخة)` : '', ...(src.label.en ? { en: `${src.label.en} (copy)` } : {}) },
    };
    const at = fields.findIndex((f) => f.key === key) + 1;
    update(renumber([...fields.slice(0, at), copy, ...fields.slice(at)]));
    setSelected(k);
    toast(tr('تم تكرار الحقل', 'Field duplicated'));
  };
  const labelOf = (k: string) => {
    const f = fields.find((x) => x.key === k);
    return (isEn() ? f?.label.en : f?.label.ar) || f?.label.ar || k;
  };
  const dependents = current ? fields.filter((f) => [...collectConditions(f.visibleWhen), ...collectConditions(f.requiredWhen)].some((c) => c.field === current.key)) : [];
  const draft = draftQ.data;
  const previewFields = draft ? fields : (publishedQ.data?.fields ?? []);
  const togglePreview = () => {
    if (!wide) {
      setPreviewDrawer(true);
      return;
    }
    const v = !previewOn;
    setPreviewOn(v);
    try {
      localStorage.setItem(PREVIEW_KEY, v ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  // JSON: a full copy of the fields (export) / replace the draft's fields with a file (import, not saved until «حفظ»)
  const importInput = useRef<HTMLInputElement>(null);
  const [pendingImport, setPendingImport] = useState<{ name: string; fields: FormField[] } | null>(null);
  const onImportFile = (file: File | undefined) => {
    if (!file) return;
    readFormJson(file)
      .then((imported) => setPendingImport({ name: file.name, fields: imported }))
      .catch(toastError)
      .finally(() => {
        if (importInput.current) importInput.current.value = '';
      });
  };

  const moreItems: ActionMenuItem[] = [
    { label: tr('معاينة في صفحة كاملة', 'Full-page preview'), icon: <OpenInNewIcon fontSize="small" />, onClick: () => openFormPreview(nav, id!, previewFields), disabled: !lookups.data || previewFields.length === 0 },
    {
      label: tr('تصدير Excel للمراجعة', 'Export to Excel for review'),
      icon: <TableViewIcon fontSize="small" />,
      disabled: !lookups.data || previewFields.length === 0,
      onClick: () => {
        import('./exportExcel')
          .then((m) => m.exportFormWorkbook(form?.name ?? 'form', draft?.version_no ?? form?.published_version ?? undefined, previewFields, lookups.data!))
          .then(() => toast(tr('تم تنزيل ملف Excel', 'Excel file downloaded')))
          .catch(toastError);
      },
    },
    {
      label: tr('تنزيل JSON', 'Download JSON'),
      icon: <FileDownloadOutlinedIcon fontSize="small" />,
      disabled: previewFields.length === 0,
      onClick: () => downloadFormJson(form?.name ?? 'form', draft ? draft.version_no : (form?.published_version ?? null), !!draft, previewFields),
    },
    ...(draft ? [{ label: tr('استيراد JSON إلى المسودة', 'Import JSON into the draft'), icon: <FileUploadOutlinedIcon fontSize="small" />, onClick: () => importInput.current?.click() }] : []),
    { label: tr('النسخ', 'Versions'), icon: <HistoryIcon fontSize="small" />, onClick: () => setHistoryOpen(true) },
    ...(draft ? [{ label: tr('حذف المسودة', 'Delete draft'), icon: <DeleteOutlineIcon fontSize="small" color="error" />, onClick: () => setConfirm('discard'), disabled: !form?.published_version_id, danger: true, dividerBefore: true }] : []),
  ];

  if (draftQ.isLoading || forms.isLoading) return <Loading />;
  if (draftQ.isError) return <ErrorState error={draftQ.error} onRetry={() => draftQ.refetch()} />;

  const status = draft
    ? tr(`مسودة v${draft.version_no}${form?.published_version ? ` · المنشور v${form.published_version}` : ''}`, `Draft v${draft.version_no}${form?.published_version ? ` · live v${form.published_version}` : ''}`)
    : form?.published_version
      ? tr(`النسخة المنشورة v${form.published_version}`, `Published version v${form.published_version}`)
      : tr('بدون نسخة منشورة', 'No published version');

  const titleRow = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: { xs: '100%', md: 0 }, flex: 1 }}>
      <Tooltip title={tr('كل النماذج', 'All forms')}>
        <IconButton size="small" onClick={() => nav('/forms')}>{isEn() ? <ArrowBackIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}</IconButton>
      </Tooltip>
      <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>{form?.name ?? tr('النموذج', 'Form')}</Typography>
      <Chip size="small" variant="outlined" label={status} />
      {draft && dirty && <Chip size="small" color="warning" label={tr('تغييرات غير محفوظة', 'Unsaved changes')} />}
    </Stack>
  );

  if (!draft) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <PageActionsMenu label={tr('إجراءات النموذج', 'Form actions')} items={moreItems} />
        <Box sx={{ mb: 2 }}>{titleRow}</Box>
        <Alert severity="info" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => createDraft.mutate()} loading={createDraft.isPending}>{tr('إنشاء مسودة للتعديل', 'Create a draft to edit')}</Button>}>
          {tr('النسخة المنشورة ثابتة ولا تُعدّل. لتغيير النموذج أنشئ مسودة (نسخة من المنشور)، عدّلها، ثم انشرها كنسخة جديدة.', 'The published version is locked and can’t be edited. To change the form, create a draft (a copy of the published version), edit it, then publish it as a new version.')}
        </Alert>
        {lookups.data && previewFields.length > 0 && (
          <Pane title={tr('النموذج المنشور', 'Published form')} sx={{ maxWidth: 720 }}>
            <FormPreviewPane fields={previewFields} lookups={lookups.data} />
          </Pane>
        )}
        <VersionsDialog formId={id!} open={historyOpen} onClose={() => setHistoryOpen(false)} />
      </Box>
    );
  }

  const showPreview = previewOn && !!lookups.data;
  const preview = lookups.data ? <FormPreviewPane fields={fields} lookups={lookups.data} /> : null;
  const issues = allErrors.length;

  return (
    <BilingualScope>
      <PageActionsMenu label={tr('المزيد', 'More')} items={moreItems} />
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: { xs: 'auto', md: 'hidden' }, pb: { xs: 2, md: 1.5 } }}>
        {/* toolbar */}
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
          {titleRow}
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="build" sx={{ gap: 0.75, px: 1.5 }}><ViewListIcon fontSize="small" />{tr('الحقول', 'Fields')}</ToggleButton>
            <ToggleButton value="scenario" sx={{ gap: 0.75, px: 1.5 }}><AltRouteIcon fontSize="small" />{tr('حسب الحالة', 'By case')}</ToggleButton>
            <ToggleButton value="matrix" sx={{ gap: 0.75, px: 1.5 }}><GridOnIcon fontSize="small" />{tr('خريطة الظهور', 'Visibility map')}</ToggleButton>
          </ToggleButtonGroup>
          {view === 'build' && <Tooltip title={previewOn ? tr('إخفاء المعاينة', 'Hide preview') : tr('إظهار المعاينة', 'Show preview')}>
            <ToggleButton size="small" value="preview" selected={wide ? previewOn : previewDrawer} onChange={togglePreview} sx={{ gap: 0.75, px: 1.5 }}>
              <ViewSidebarIcon fontSize="small" />{tr('المعاينة', 'Preview')}
            </ToggleButton>
          </Tooltip>}
          <Button size="small" variant="outlined" color={issues ? 'error' : 'success'} startIcon={issues ? <ErrorOutlineIcon /> : <CheckCircleIcon />} onClick={() => setIssuesOpen(true)}>
            {issues ? tr(`${issues} مشكلة`, `${issues} issue${issues === 1 ? '' : 's'}`) : tr('جاهز للنشر', 'Ready')}
          </Button>
          <Tooltip title="Ctrl + S">
            <span><Button size="small" variant="outlined" startIcon={<SaveIcon />} onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>{tr('حفظ', 'Save')}</Button></span>
          </Tooltip>
          <Button size="small" variant="contained" startIcon={<PublishIcon />} onClick={() => setConfirm('publish')} disabled={issues > 0} loading={publish.isPending}>{tr('نشر', 'Publish')}</Button>
        </Stack>

        {view === 'scenario' ? (
          <Box sx={{ flex: 1, minHeight: { xs: 480, md: 0 }, display: 'flex', flexDirection: 'column' }}>
            <ScenarioView fields={fields} lookups={lookups.data} onChange={update} onOpen={(k) => { setSelected(k); setView('build'); }} />
          </Box>
        ) : view === 'matrix' ? (
          <Box sx={{ flex: 1, minHeight: { xs: 480, md: 0 }, display: 'flex', flexDirection: 'column' }}>
            <VisibilityMatrix fields={fields} lookups={lookups.data} onChange={update} onOpen={(k) => { setSelected(k); setView('build'); }} />
          </Box>
        ) : (
          <Box sx={{
            flex: 1, minHeight: 0, display: 'grid', gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: '300px minmax(0,1fr)', lg: showPreview && wide ? '290px minmax(0,1fr) 400px' : '320px minmax(0,1fr)' },
            gridTemplateRows: { xs: 'auto', md: 'minmax(0,1fr)' },
          }}>
            <Pane
              title={`${tr('الحقول', 'Fields')} (${fields.length})`}
              actions={<Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>{tr('حقل', 'Field')}</Button>}
              sx={{ maxHeight: { xs: 420, md: 'none' } }}
            >
              <FieldList fields={fields} selected={selected} issueKeys={issueKeys} onSelect={setSelected} onReorder={(next) => update(renumber(next))}
                onDuplicate={duplicate} onDelete={(k) => { setSelected(k); setConfirm('delete'); }} describe={(f) => describeRule(f.visibleWhen ?? f.requiredWhen ?? null, labelOf)} />
            </Pane>
            <Pane>
              <BilingualToolbar counts={{ ar: 0, en: translation.length }} sx={{ mb: 2 }} />
              {current ? (
                <FieldEditor
                  key={current.key}
                  field={current}
                  fields={fields}
                  lookups={lookups.data}
                  issues={allErrors.concat(live.warnings).filter((e) => e.field === current.key)}
                  onChange={(f) => {
                    const oldKey = current.key;
                    let next = fields.map((x) => (x.key === oldKey ? f : x));
                    if (f.key !== oldKey) {
                      next = next.map((x) => ({
                        ...x,
                        visibleWhen: x.visibleWhen ? JSON.parse(JSON.stringify(x.visibleWhen).replaceAll(`"field":"${oldKey}"`, `"field":"${f.key}"`)) : x.visibleWhen,
                        requiredWhen: x.requiredWhen ? JSON.parse(JSON.stringify(x.requiredWhen).replaceAll(`"field":"${oldKey}"`, `"field":"${f.key}"`)) : x.requiredWhen,
                        config: x.config.options?.some((o) => o.visibleWhen) ? JSON.parse(JSON.stringify(x.config).replaceAll(`"field":"${oldKey}"`, `"field":"${f.key}"`)) : x.config,
                      }));
                      setSelected(f.key);
                    }
                    update(next);
                  }}
                  onDelete={() => setConfirm('delete')}
                  onDuplicate={() => duplicate(current.key)}
                />
              ) : (
                <Stack alignItems="center" spacing={2} sx={{ py: 8, color: 'text.secondary' }}>
                  <Typography>{tr('اختر حقلاً من القائمة لتعديله', 'Select a field from the list to edit it')}</Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAdding(true)}>{tr('إضافة حقل', 'Add field')}</Button>
                </Stack>
              )}
            </Pane>
            {showPreview && wide && <Pane title={tr('معاينة مباشرة', 'Live preview')}>{preview}</Pane>}
          </Box>
        )}
      </Box>

      {/* preview on narrower screens */}
      <Drawer anchor={isEn() ? 'right' : 'left'} open={!wide && previewDrawer && !!lookups.data} onClose={() => setPreviewDrawer(false)} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 }, p: 2 } } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>{tr('معاينة مباشرة', 'Live preview')}</Typography>
          <IconButton onClick={() => setPreviewDrawer(false)}><CloseIcon /></IconButton>
        </Stack>
        {preview}
      </Drawer>

      <Drawer anchor={isEn() ? 'right' : 'left'} open={issuesOpen} onClose={() => setIssuesOpen(false)} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 }, p: 2 } } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>{tr('التحقق قبل النشر', 'Checks before publishing')}</Typography>
          <IconButton onClick={() => setIssuesOpen(false)}><CloseIcon /></IconButton>
        </Stack>
        <ValidationPane
          errors={allErrors}
          warnings={live.warnings}
          onPick={(k) => {
            if (k) {
              setSelected(k);
              setView('build');
            }
            setIssuesOpen(false);
          }}
          labelOf={labelOf}
          onAutoFix={allErrors.some((e) => AUTO_FIXABLE.has((e as { code?: string }).code ?? '')) ? () => update(withMandatoryFields(fields)) : undefined}
        />
      </Drawer>
      <AddFieldDialog open={adding} onClose={() => setAdding(false)} onPick={addField} fields={fields} />

      <ConfirmDialog
        open={confirm === 'publish'}
        title={tr('نشر النموذج', 'Publish form')}
        text={tr('ستصبح هذه المسودة النسخة الحالية لكل الشركات المرتبطة. النسخة السابقة تُؤرشف وتبقى الإدخالات القديمة مرتبطة بها، وتُقبل الإرسالات عليها لمدة 24 ساعة.', 'This draft will become the current version for all linked companies. The previous version is archived, existing entries stay linked to it, and submissions against it are still accepted for 24 hours.')}
        confirmLabel={tr('نشر', 'Publish')}
        busy={publish.isPending}
        onClose={() => setConfirm(null)}
        onConfirm={() => publish.mutate()}
      >
        {live.warnings.length > 0 && <Alert severity="warning" sx={{ mt: 2 }}>{tr(`${live.warnings.length} تحذير — راجع قائمة التحقق.`, `${live.warnings.length} warning(s) — check the issues list.`)}</Alert>}
      </ConfirmDialog>
      <ConfirmDialog open={confirm === 'discard'} danger title={tr('حذف المسودة', 'Delete draft')} text={tr('ستفقد كل التعديلات غير المنشورة.', 'All unpublished changes will be lost.')} busy={discard.isPending} onClose={() => setConfirm(null)} onConfirm={() => discard.mutate()} />
      <ConfirmDialog
        open={confirm === 'delete'}
        danger
        title={tr('حذف الحقل', 'Delete field')}
        text={dependents.length
          ? tr(`هذا الحقل مستخدم في شروط: ${dependents.map((d) => d.label.ar || d.key).join('، ')}. يجب تعديل تلك الشروط قبل النشر.`, `This field is used in conditions of: ${dependents.map((d) => labelOf(d.key)).join(', ')}. Update those conditions before publishing.`)
          : tr(`حذف «${current?.label.ar || current?.key}»؟`, `Delete “${current ? labelOf(current.key) : ''}”?`)}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const idx = fields.findIndex((f) => f.key === current?.key);
          const next = renumber(fields.filter((f) => f.key !== current?.key));
          update(next);
          setSelected(next[Math.max(0, idx - 1)]?.key ?? null);
          setConfirm(null);
        }}
      />
      <input ref={importInput} hidden type="file" accept="application/json,.json" onChange={(e) => onImportFile(e.target.files?.[0])} />
      <ConfirmDialog
        open={!!pendingImport}
        title={tr('استيراد JSON', 'Import JSON')}
        text={tr(
          `سيحلّ محتوى «${pendingImport?.name ?? ''}» (${pendingImport?.fields.length ?? 0} حقل) محل حقول المسودة الحالية. لا يُحفظ شيء حتى تضغط «حفظ»، ويمكنك المراجعة أولاً.`,
          `The ${pendingImport?.fields.length ?? 0} fields of “${pendingImport?.name ?? ''}” will replace the draft's fields. Nothing is saved until you press Save, so you can review first.`,
        )}
        confirmLabel={tr('استيراد', 'Import')}
        onClose={() => setPendingImport(null)}
        onConfirm={() => {
          const next = pendingImport!.fields;
          update(next);
          setSelected(next[0]?.key ?? null);
          setPendingImport(null);
          toast(tr('تم الاستيراد — راجع ثم احفظ', 'Imported — review, then save'));
        }}
      />
      <VersionsDialog formId={id!} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </BilingualScope>
  );
}

function ValidationPane({ errors, warnings, onPick, labelOf, onAutoFix }: { errors: { field?: string; message: string }[]; warnings: { field?: string; message: string }[]; onPick: (k?: string) => void; labelOf: (k: string) => string; onAutoFix?: () => void }) {
  if (!errors.length && !warnings.length) return <Alert severity="success">{tr('النموذج صالح للنشر.', 'The form is ready to publish.')}</Alert>;
  return (
    <List dense disablePadding>
      {onAutoFix && (
        <Alert severity="info" sx={{ mb: 1 }} action={<Button color="inherit" size="small" onClick={onAutoFix}>{tr('إصلاح تلقائي', 'Auto-fix')}</Button>}>
          {tr('كل نموذج يحتاج: «نوع الطلب» أولاً وإلزامياً، و«الاسم» و«رقم الهاتف» كحقول إلزامية.', 'Every form needs “Request type” first and required, plus “Name” and “Phone number” as required fields.')}
        </Alert>
      )}
      {errors.map((e, i) => (
        <ListItem key={`e${i}`} onClick={() => onPick(e.field)} sx={{ cursor: e.field ? 'pointer' : undefined }}>
          <ListItemText primary={e.message} secondary={e.field ? labelOf(e.field) : tr('النموذج', 'Form')} slotProps={{ primary: { color: 'error' } }} />
        </ListItem>
      ))}
      {warnings.map((w, i) => (
        <ListItem key={`w${i}`} onClick={() => onPick(w.field)} sx={{ cursor: w.field ? 'pointer' : undefined }}>
          <ListItemText primary={w.message} secondary={w.field ? labelOf(w.field) : undefined} slotProps={{ primary: { color: 'warning.main' } }} />
        </ListItem>
      ))}
    </List>
  );
}

function VersionsDialog({ formId, open, onClose }: { formId: string; open: boolean; onClose: () => void }) {
  const q = useQuery({ queryKey: ['form-versions', formId], queryFn: () => api<{ items: FormVersion[] }>(`/forms/${formId}/versions`), enabled: open });
  const color = { DRAFT: 'warning', PUBLISHED: 'success', ARCHIVED: 'default' } as const;
  const label = { DRAFT: tr('مسودة', 'Draft'), PUBLISHED: tr('منشورة', 'Published'), ARCHIVED: tr('مؤرشفة', 'Archived') };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{tr('نسخ النموذج', 'Form versions')}</DialogTitle>
      <DialogContent dividers>
        {q.isLoading ? <Loading /> : (
          <List dense>
            {(q.data?.items ?? []).map((v) => (
              <ListItem key={v.id} secondaryAction={<Chip size="small" color={color[v.status]} label={label[v.status]} />}>
                <ListItemText primary={`v${v.version_no}${v.notes ? ` — ${v.notes}` : ''}`} secondary={
                  v.published_at
                    ? tr(`نُشرت ${fmtDate(v.published_at)} · ${v.submissions_count} إدخال`, `Published ${fmtDate(v.published_at)} · ${v.submissions_count} entr${v.submissions_count === 1 ? 'y' : 'ies'}`)
                    : tr(`أُنشئت ${fmtDate(v.created_at)} · ${v.submissions_count} إدخال`, `Created ${fmtDate(v.created_at)} · ${v.submissions_count} entr${v.submissions_count === 1 ? 'y' : 'ies'}`)
                } />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
